import { useEffect, useRef } from "react"
import classNames from "classnames"
import { type GatsbyLinkProps, Link } from "gatsby"
import * as styles from "@/styles/modules/Button.module.scss"

interface ButtonBase {
  className?: string
  loading?: boolean
  size?: "small" | "large"
  uiType?: "black" | "secondary" | "underline"
}

interface AnchorButton
  extends ButtonBase,
    React.AnchorHTMLAttributes<HTMLAnchorElement> {
  onClick?: React.MouseEventHandler<HTMLAnchorElement>
  href: string
}

interface LinkButton extends ButtonBase, GatsbyLinkProps<any> {
  onClick?: never
}

interface ButtonButton
  extends ButtonBase,
    React.ButtonHTMLAttributes<HTMLButtonElement> {
  dataId?: string
  autoFocus?: boolean
  onClick?: React.MouseEventHandler<HTMLButtonElement>
}

const Button = (
  props: React.PropsWithChildren<AnchorButton | LinkButton | ButtonButton>,
) => {
  const { autoFocus, children, className, loading, size, uiType } = props

  const btnRef = useRef<HTMLAnchorElement | HTMLButtonElement>(null)

  useEffect(() => {
    if (autoFocus && btnRef.current) {
      btnRef.current.focus()
    }
  }, [autoFocus])

  // const classes = [
  //   styles.button,
  //   uiType ? styles[uiType] : "",
  //   size === "small" || size === "large" ? styles[size] : "",
  //   loading ? styles.loading : "",
  //   className ? className : "",
  // ].join(" ")

  const classes = classNames(
    styles.button,
    {
      [styles[uiType!]]: uiType,
      [styles[size!]]: size,
      [styles.loading]: loading,
    },
    className,
  )

  if ("to" in props) {
    const { to, state, title } = props
    return (
      <Link
        to={to}
        className={classes}
        state={state}
        title={title}
        ref={btnRef as any}
      >
        {loading && <span className={styles.loadingSpinner} />}
        <div className={styles.content}>{children}</div>
      </Link>
    )
  }

  if ("href" in props) {
    const { href, title, target, onClick } = props
    return (
      <a
        className={classes}
        href={href}
        title={title}
        target={target}
        onClick={onClick}
        ref={btnRef as React.RefObject<HTMLAnchorElement>}
        rel="noreferrer"
      >
        {loading && <span className={styles.loadingSpinner} />}
        <div className={styles.content}>{children}</div>
      </a>
    )
  }

  const { onClick, onFocus, onBlur, type, dataId } = props

  return (
    <button
      className={classes}
      onClick={onClick}
      onFocus={onFocus}
      onBlur={onBlur}
      disabled={loading}
      ref={btnRef as React.RefObject<HTMLButtonElement>}
      type={type}
      data-id={dataId}
    >
      {loading && <span className={styles.loadingSpinner} />}
      <div className={styles.content}>{children}</div>
    </button>
  )
}

export const ButtonGroup = ({
  className,
  children,
}: React.PropsWithChildren<{ className?: string }>) => {
  return (
    <div className={classNames(styles.buttonGroup, className)}>{children}</div>
  )
}

export default Button
